// ============================================================================
// KANBAN BOARD TYPES
// ============================================================================

/**
 * Kanban column status
 */
export type KanbanStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';

/**
 * Task priority in Kanban
 */
export type KanbanPriority = 'low' | 'medium' | 'high' | 'urgent';

/**
 * Kanban card (represents a task)
 */
export interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  status: KanbanStatus;
  priority: KanbanPriority;

  // Task details
  assignedTo?: string[]; // Member IDs
  estimatedHours?: number;
  actualHours?: number;
  dueDate?: Date;

  // Related entities
  pertNodeId?: string; // Link to PERT task
  tags: string[];
  checklist?: KanbanChecklistItem[];

  // Metadata
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  order: number; // Position in column

  // Additional info
  color?: string;
  blockedBy?: string[]; // IDs of blocking cards
  blockers?: string; // Reason for being blocked
}

/**
 * Checklist item within a card
 */
export interface KanbanChecklistItem {
  id: string;
  text: string;
  completed: boolean;
  createdAt: Date;
}

/**
 * Kanban column
 */
export interface KanbanColumn {
  id: KanbanStatus;
  title: string;
  cards: KanbanCard[];
  wipLimit?: number; // Work In Progress limit
  color: string;
  order: number;
}

/**
 * Kanban board
 */
export interface KanbanBoard {
  id: string;
  projectId: string;
  columns: KanbanColumn[];
  lastUpdated: Date;
  createdAt: Date;
}

/**
 * Kanban filter options
 */
export interface KanbanFilter {
  assignees?: string[];
  priorities?: KanbanPriority[];
  tags?: string[];
  dueDate?: {
    from?: Date;
    to?: Date;
  };
  searchQuery?: string;
  showCompleted?: boolean;
}

/**
 * Kanban statistics
 */
export interface KanbanStatistics {
  totalCards: number;
  byStatus: Record<KanbanStatus, number>;
  byPriority: Record<KanbanPriority, number>;
  completionRate: number;
  averageCycleTime?: number; // Days from todo to done
  cardsOverdue: number;
  cardsBlocked: number;
  totalEstimatedHours: number;
  totalActualHours: number;
}

/**
 * Kanban configuration
 */
export interface KanbanConfig {
  // Column settings
  columns: {
    [K in KanbanStatus]: {
      enabled: boolean;
      title: string;
      wipLimit?: number;
      color: string;
    };
  };

  // Display settings
  showCardNumbers: boolean;
  showAssignees: boolean;
  showPriority: boolean;
  showDueDate: boolean;
  showEstimatedHours: boolean;
  compactMode: boolean;

  // Behavior
  enableWipLimits: boolean;
  autoArchiveCompleted: boolean;
  autoArchiveDays: number;

  // Notifications
  notifyOnCardAssigned: boolean;
  notifyOnDueDateApproaching: boolean;
  notifyDaysBeforeDue: number;

  // Filters
  filters: {
    priorities: KanbanPriority[];
    assignees: string[];
    tags: string[];
    searchQuery: string;
    showOverdueOnly: boolean;
    showBlockedOnly: boolean;
  };
}

/**
 * Default Kanban configuration
 */
export const DEFAULT_KANBAN_CONFIG: KanbanConfig = {
  columns: {
    backlog: {
      enabled: true,
      title: 'Backlog',
      color: '#6b7280',
    },
    todo: {
      enabled: true,
      title: 'À faire',
      color: '#3b82f6',
    },
    in_progress: {
      enabled: true,
      title: 'En cours',
      wipLimit: 5,
      color: '#f59e0b',
    },
    review: {
      enabled: true,
      title: 'En revue',
      wipLimit: 3,
      color: '#8b5cf6',
    },
    done: {
      enabled: true,
      title: 'Terminé',
      color: '#10b981',
    },
  },
  showCardNumbers: true,
  showAssignees: true,
  showPriority: true,
  showDueDate: true,
  showEstimatedHours: true,
  compactMode: false,
  enableWipLimits: true,
  autoArchiveCompleted: false,
  autoArchiveDays: 30,
  notifyOnCardAssigned: true,
  notifyOnDueDateApproaching: true,
  notifyDaysBeforeDue: 2,
  filters: {
    priorities: [],
    assignees: [],
    tags: [],
    searchQuery: '',
    showOverdueOnly: false,
    showBlockedOnly: false,
  },
};

/**
 * Kanban card template
 */
export interface KanbanCardTemplate {
  id: string;
  name: string;
  description: string;
  priority: KanbanPriority;
  estimatedHours?: number;
  checklist?: string[];
  tags: string[];
}

/**
 * Common Kanban card templates
 */
export const KANBAN_CARD_TEMPLATES: KanbanCardTemplate[] = [
  {
    id: 'bug-fix',
    name: 'Correction de Bug',
    description: 'Identifier, reproduire et corriger un bug',
    priority: 'high',
    estimatedHours: 4,
    checklist: [
      'Reproduire le bug',
      'Identifier la cause',
      'Implémenter la correction',
      'Tester la correction',
      'Mettre à jour la documentation',
    ],
    tags: ['bug', 'correction'],
  },
  {
    id: 'feature',
    name: 'Nouvelle Fonctionnalité',
    description: 'Développer une nouvelle fonctionnalité',
    priority: 'medium',
    estimatedHours: 16,
    checklist: [
      'Analyser les besoins',
      'Designer la solution',
      'Implémenter la fonctionnalité',
      'Écrire les tests',
      'Documenter',
      'Code review',
    ],
    tags: ['feature', 'développement'],
  },
  {
    id: 'tech-debt',
    name: 'Dette Technique',
    description: 'Refactoring et amélioration du code',
    priority: 'low',
    estimatedHours: 8,
    checklist: [
      'Identifier la zone à refactorer',
      'Planifier le refactoring',
      'Implémenter les changements',
      'Vérifier les tests existants',
      'Documenter les changements',
    ],
    tags: ['tech-debt', 'refactoring'],
  },
  {
    id: 'documentation',
    name: 'Documentation',
    description: 'Créer ou mettre à jour la documentation',
    priority: 'medium',
    estimatedHours: 4,
    checklist: [
      'Identifier les sections à documenter',
      'Rédiger le contenu',
      'Ajouter des exemples',
      'Faire relire',
      'Publier',
    ],
    tags: ['documentation'],
  },
  {
    id: 'testing',
    name: 'Tests',
    description: 'Écrire ou améliorer les tests',
    priority: 'medium',
    estimatedHours: 6,
    checklist: [
      'Identifier les cas de test',
      'Écrire les tests unitaires',
      'Écrire les tests d\'intégration',
      'Vérifier la couverture',
      'Documenter les tests',
    ],
    tags: ['tests', 'qualité'],
  },
];

/**
 * Drag and drop result
 */
export interface DragResult {
  cardId: string;
  sourceStatus: KanbanStatus;
  destinationStatus: KanbanStatus;
  sourceIndex: number;
  destinationIndex: number;
}

/**
 * Column info for display
 */
export interface KanbanColumnInfo {
  id: KanbanStatus;
  title: string;
  description: string;
  color: string;
  icon: string;
  order: number;
}

/**
 * Default column definitions
 */
export const KANBAN_COLUMNS: KanbanColumnInfo[] = [
  {
    id: 'backlog',
    title: 'Backlog',
    description: 'Tâches à prioriser',
    color: '#6b7280',
    icon: 'FiInbox',
    order: 0,
  },
  {
    id: 'todo',
    title: 'À faire',
    description: 'Tâches prêtes à démarrer',
    color: '#3b82f6',
    icon: 'FiList',
    order: 1,
  },
  {
    id: 'in_progress',
    title: 'En cours',
    description: 'Tâches en cours de réalisation',
    color: '#f59e0b',
    icon: 'FiPlay',
    order: 2,
  },
  {
    id: 'review',
    title: 'En revue',
    description: 'Tâches en cours de validation',
    color: '#8b5cf6',
    icon: 'FiEye',
    order: 3,
  },
  {
    id: 'done',
    title: 'Terminé',
    description: 'Tâches complétées',
    color: '#10b981',
    icon: 'FiCheckCircle',
    order: 4,
  },
];
